import { useBasename, useNotify } from "ra-core";
import { useNavigate } from "react-router";
import { authProvider } from "./auth";
import { errorMessage } from "./utils";
import { useCheckAuth } from "ra-core";
import appleLoginImage from "./assets/apple-id-sign-in.png";
import appIcon from "./assets/AppIcon.png";
import { useEffect, useRef, useState } from "react";
import { Card, styled, TextField, Button, Typography, Alert } from "@mui/material";
import { Loading } from "react-admin";

export const AuthCallbackPage = () => {
    const navigate = useNavigate();
    const callbackCalled = useRef(false);

    useEffect(() => {
        if (!authProvider.handleCallback) return;

        // Fix duplicate call on dev
        if (callbackCalled.current) return;
        callbackCalled.current = true;

        authProvider
            .handleCallback()
            .then(() => {
                navigate("/", { replace: true });
                window.history.replaceState({}, "", "/");
            })
            .catch((error) => {
                window.alert(error);
            });
    }, [callbackCalled]);

    return <Loading loadingPrimary="" loadingSecondary="" />;
};

interface GoogleSignInButtonProps {
    onClick?: () => void;
}

const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = ({ onClick }) => {
    return (
        <button
            onClick={onClick}
            type="button"
            style={{
                display: "flex",
                alignItems: "center",
                backgroundColor: "#4285f4",
                border: "2px solid #4285f4",
                borderRadius: "4px",
                color: "white",
                cursor: "pointer",
                fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
                fontSize: "16px",
                fontWeight: "500",
                height: "50px",
                minWidth: "240px",
                outline: "none",
                padding: "0",
                paddingLeft: "25px",
                transition: "background-color 0.2s ease",
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#3367d6";
                e.currentTarget.style.borderColor = "#3367d6";
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#4285f4";
                e.currentTarget.style.borderColor = "#4285f4";
            }}
            onMouseDown={(e) => {
                e.currentTarget.style.backgroundColor = "#2d5aa0";
            }}
            onMouseUp={(e) => {
                e.currentTarget.style.backgroundColor = "#3367d6";
            }}
        >
            <div
                style={{
                    backgroundColor: "white",
                    padding: "12px",
                    borderRadius: "2px 0 0 2px",
                    height: "32px",
                    width: "32px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <svg width="18" height="18" viewBox="0 0 18 18" style={{ flexShrink: 0 }}>
                    <path
                        fill="#4285F4"
                        d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18Z"
                    />
                    <path
                        fill="#34A853"
                        d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2.04a4.8 4.8 0 0 1-2.7.75c-2.09 0-3.86-1.4-4.49-3.29H1.83v2.07A8 8 0 0 0 8.98 17Z"
                    />
                    <path
                        fill="#FBBC04"
                        d="M4.49 10.48A4.8 4.8 0 0 1 4.25 9c0-.51.08-1.01.24-1.48V5.45H1.83A8 8 0 0 0 .98 9c0 1.3.31 2.52.85 3.61l2.66-2.13Z"
                    />
                    <path
                        fill="#EA4335"
                        d="M8.98 3.58c1.18 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 0 0 8.98.98a8 8 0 0 0-7.15 4.47l2.66 2.07c.63-1.89 2.4-3.29 4.49-3.29Z"
                    />
                </svg>
            </div>
            <span style={{ paddingLeft: "8px", paddingRight: "24px" }}>Sign in with Google</span>
        </button>
    );
};

export const CustomLogin = () => {
    const checkAuth = useCheckAuth();
    const navigate = useNavigate();
    const basename = useBasename();
    const notify = useNotify();
    const [email, setEmail] = useState("");
    const [emailLinkSent, setEmailLinkSent] = useState(false);
    const [emailLinkLoading, setEmailLinkLoading] = useState(false);
    const [authChecked, setAuthChecked] = useState(false);

    useEffect(() => {
        if (authChecked) return;

        checkAuth({}, false)
            .then(() => {
                setAuthChecked(true);
                navigate("/");
            })
            .catch(() => {
                setAuthChecked(true);
                // not authenticated, stay on the login page
            });
    }, [checkAuth, navigate, authChecked]);

    const handleSendEmailLink = async () => {
        if (!email) {
            notify("Please enter your email address", { type: "error" });
            return;
        }

        setEmailLinkLoading(true);
        try {
            await authProvider.sendEmailLink(email);
            setEmailLinkSent(true);
            notify("Sign-in link sent to your email!", { type: "success" });
        } catch (error: any) {
            notify(errorMessage(error, "Failed to send email link"), { type: "error" });
        } finally {
            setEmailLinkLoading(false);
        }
    };

    return (
        <Root>
            <Card className={LoginClasses.card}>
                <div className={LoginClasses.icon}>
                    <img src={appIcon} style={{ width: "64px", height: "auto", borderRadius: "10%" }} />
                </div>
                <div className={LoginClasses.methods}>
                    <h3 style={{ marginTop: "0" }}>Carbon</h3>

                    {/* Email Link Authentication */}

                    <GoogleSignInButton
                        onClick={() => {
                            authProvider
                                .loginWithGoogle()
                                .then(() => {
                                    navigate(`${basename}/`);
                                })
                                .catch((error: any) => {
                                    notify(errorMessage(error, "ra.auth.sign_in_error"), { type: "error" });
                                });
                        }}
                    />
                    <div
                        onClick={() => {
                            authProvider
                                .loginWithApple()
                                .then(() => {
                                    navigate(`${basename}/`);
                                })
                                .catch((error: any) => {
                                    notify(errorMessage(error, "ra.auth.sign_in_error"), { type: "error" });
                                });
                        }}
                        style={{
                            transition: "transform 0.2s ease",
                            cursor: "pointer",
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "scale(1.02)";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "scale(1)";
                        }}
                    >
                        <img
                            src={appleLoginImage}
                            style={{ width: "240px", height: "auto", display: "block" }}
                        />
                    </div>

                    <Typography variant="body2" style={{ margin: "10px 0", color: "#666" }}>
                        or
                    </Typography>

                    <div style={{ width: "240px", marginBottom: "10px" }}>
                        <TextField
                            label="Email"
                            type="email"
                            fullWidth
                            size="small"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={emailLinkSent}
                            style={{ marginBottom: "8px" }}
                        />
                        {emailLinkSent ? (
                            <Alert severity="info" style={{ fontSize: "0.875rem" }}>
                                Check your email for the sign-in link
                            </Alert>
                        ) : (
                            <Button
                                variant="outlined"
                                fullWidth
                                onClick={handleSendEmailLink}
                                disabled={emailLinkLoading}
                                style={{ textTransform: "none" }}
                            >
                                {emailLinkLoading ? "Sending..." : "Send sign-in link"}
                            </Button>
                        )}
                    </div>
                </div>
            </Card>
        </Root>
    );
};

const PREFIX = "WaveLogin";
export const LoginClasses = {
    card: `${PREFIX}-card`,
    icon: `${PREFIX}-icon`,
    methods: `${PREFIX}-methods`,
};

const Root = styled("div", {
    name: PREFIX,
    overridesResolver: (props, styles) => styles.root,
})(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    height: "1px",
    alignItems: "center",
    justifyContent: "flex-start",
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    backgroundImage: "radial-gradient(circle at 50% 14em, #313264 0%, #00023b 60%, #00023b 100%)",

    [`& .${LoginClasses.card}`]: {
        minWidth: 300,
        marginTop: "6em",
    },
    [`& .${LoginClasses.icon}`]: {
        margin: "1em",
        display: "flex",
        justifyContent: "center",
    },
    [`& .${LoginClasses.methods}`]: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        margin: "10px",
        gap: "5px",
    },
}));
